import { Linkedin, Mail, Phone, Scale } from "lucide-react";
import { Link } from "react-router-dom";
import Reveal from "./components/landing/Reveal";
import Countdown from "./components/landing/Countdown";
import EyebrowCountdown from "./components/landing/EyebrowCountdown";
import DocPreview from "./components/landing/DocPreview";
import Timeline from "./components/landing/Timeline";
import HowItWorks from "./components/landing/HowItWorks";
import {
  PrimaryButton,
  SecondaryButton,
  NavGhostButton,
  NavPrimaryButton,
  CAL_URL,
} from "./components/landing/CTAs";
import "@/App.css";

const Wordmark = () => (
  <Link
    to="/"
    data-testid="brand-wordmark"
    className="inline-flex items-baseline gap-2 select-none"
    aria-label="Adheran — home"
  >
    <span
      aria-hidden="true"
      className="inline-block h-2 w-2 rounded-sm"
      style={{ background: "var(--blue)" }}
    />
    <span className="text-[15px] font-semibold tracking-[0.16em] uppercase" style={{ color: "var(--ink)" }}>
      Adheran
    </span>
  </Link>
);

const Nav = () => (
  <header
    data-testid="site-header"
    className="sticky top-0 z-40 w-full border-b backdrop-blur-md"
    style={{ background: "rgba(255,255,255,0.78)", borderColor: "var(--line)" }}
  >
    <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-4 md:px-10">
      <Wordmark />
      <nav className="hidden items-center gap-8 text-sm md:flex" data-testid="site-nav">
        <a href="#problem" className="link-u text-neutral-700">The problem</a>
        <a href="#how" className="link-u text-neutral-700">How it works</a>
        <a href="#pricing" className="link-u text-neutral-700">Pricing</a>
        <a href="#founder" className="link-u text-neutral-700">Founder</a>
      </nav>
      <div className="flex items-center gap-2">
        <NavGhostButton />
        <NavPrimaryButton />
      </div>
    </div>
  </header>
);

const Hero = () => (
  <section
    id="top"
    data-testid="section-hero"
    className="relative overflow-hidden"
  >
    <div
      aria-hidden="true"
      className="grid-motif pointer-events-none absolute inset-0 opacity-60"
    />
    <div className="relative mx-auto max-w-6xl px-6 pt-16 pb-24 md:px-10 md:pt-24 md:pb-28">
      <div className="grid gap-12 lg:grid-cols-12 lg:gap-12">
        <div className="lg:col-span-7">
          <Reveal>
            <div className="sec-num" data-testid="hero-eyebrow">
              <EyebrowCountdown />
            </div>
          </Reveal>

          <Reveal delay={80}>
            <h1
              data-testid="hero-headline"
              className="display-h1 balance mt-6 text-[44px] sm:text-[60px] md:text-[76px] lg:text-[80px]"
              style={{ color: "var(--ink)" }}
            >
              EU AI Act compliance for SMEs.{" "}
              <span style={{ color: "var(--blue)" }}>€299/month,</span>{" "}
              <span style={{ color: "var(--red)" }}>not €40,000.</span>
            </h1>
          </Reveal>

          <Reveal delay={160}>
            <p
              data-testid="hero-subheadline"
              className="pretty mt-7 max-w-2xl text-lg md:text-xl"
              style={{ color: "var(--muted)" }}
            >
              Take a 5-minute risk assessment. Get audit-ready documentation. Sleep before August 2, 2026.
            </p>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-10 flex flex-col items-start gap-4">
              <PrimaryButton testId="hero-cta" />
              <SecondaryButton testId="hero-secondary-cta" />
              <p className="text-[12.5px]" data-testid="hero-cta-meta" style={{ color: "var(--muted-2)" }}>
                Pre-product · Live demo at ViennaUP 2026, May 18–22
              </p>
            </div>
          </Reveal>

          <Reveal delay={300}>
            <div className="mt-12 flex items-center gap-4 border-t pt-6" style={{ borderColor: "var(--line)" }}>
              <Countdown />
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-5">
          <div className="flex h-full items-center justify-center pt-4 lg:pt-12">
            <DocPreview />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Problem = () => (
  <section
    id="problem"
    data-testid="section-problem"
    className="border-t"
    style={{ borderColor: "var(--line)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-4">
          <Reveal>
            <div className="sec-num" data-testid="problem-eyebrow">
              <span>01 — The problem</span>
            </div>
          </Reveal>
        </div>

        <div className="md:col-span-8">
          <Reveal>
            <h2
              data-testid="problem-headline"
              className="display-h2 balance text-3xl sm:text-4xl md:text-5xl"
              style={{ color: "var(--ink)" }}
            >
              <span style={{ color: "var(--red)" }}>76%</span> of EU companies using AI in HR have not started preparing for the AI Act.
            </h2>
          </Reveal>

          <div className="mt-10 space-y-6 text-[17px] leading-relaxed md:text-lg" style={{ color: "var(--muted)" }}>
            <Reveal delay={80}>
              <p data-testid="problem-p1">
                On August 2, 2026, the EU AI Act&apos;s high-risk obligations enforce.{" "}
                <span className="font-semibold" style={{ color: "var(--ink)" }}>
                  Penalties: €35M or 7% of global revenue.
                </span>
              </p>
            </Reveal>
            <Reveal delay={140}>
              <p data-testid="problem-p2">
                Enterprise compliance vendors charge €40,000+ per year. Generic templates from your lawyer cost €5,000 and don&apos;t fit your business. Most SMEs have done nothing.
              </p>
            </Reveal>
            <Reveal delay={200}>
              <p data-testid="problem-p3" className="font-medium" style={{ color: "var(--ink)" }}>
                Adheran turns AI Act compliance from a six-figure consulting project into a subscription you can afford.
              </p>
            </Reveal>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// HowItWorks lives in components/landing/HowItWorks.jsx (4-step version with
// embedded "What the audit asks" preview). It is imported above and rendered
// from <App /> in the section order below.

const Pricing = () => (
  <section
    id="pricing"
    data-testid="section-pricing"
    className="border-t"
    style={{ borderColor: "var(--line)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-4">
          <Reveal>
            <div className="sec-num" data-testid="pricing-eyebrow">
              <span>03 — Pricing</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2
              data-testid="pricing-headline"
              className="display-h2 balance mt-5 text-3xl sm:text-4xl md:text-[44px]"
              style={{ color: "var(--ink)" }}
            >
              One tier. Flat. No hidden seats.
            </h2>
          </Reveal>
        </div>

        <div className="md:col-span-8">
          <Reveal>
            <div
              data-testid="pricing-card"
              className="relative overflow-hidden rounded-2xl border bg-white p-8 md:p-12"
              style={{ borderColor: "var(--ink)" }}
            >
              <div
                aria-hidden="true"
                className="absolute -right-10 -top-10 h-40 w-40 rounded-full"
                style={{ background: "var(--blue-50)" }}
              />
              <div className="relative">
                <div className="flex items-baseline gap-3">
                  <span
                    data-testid="pricing-price"
                    className="text-[64px] sm:text-[80px] font-extrabold tracking-tight leading-none"
                    style={{ color: "var(--ink)" }}
                  >
                    €299
                  </span>
                  <span className="text-lg font-medium" style={{ color: "var(--muted)" }}>
                    / month — flat
                  </span>
                </div>

                <p className="mt-6 max-w-xl text-[16.5px] leading-relaxed" style={{ color: "var(--muted)" }}>
                  No tiers, no hidden seats. The full Compliance Pack and quarterly regulatory updates, included.
                </p>

                <ul className="mt-7 grid grid-cols-1 gap-2.5 sm:grid-cols-2" data-testid="pricing-includes">
                  {[
                    "AI inventory register",
                    "Annex III risk assessments",
                    "Transparency notices",
                    "Governance policy",
                    "Employee guidelines",
                    "Quarterly regulatory updates",
                  ].map((item) => (
                    <li
                      key={item}
                      className="flex items-start gap-2.5 text-[15px]"
                      style={{ color: "var(--ink-2)" }}
                    >
                      <span
                        aria-hidden="true"
                        className="mt-2 inline-block h-1 w-3 flex-shrink-0"
                        style={{ background: "var(--blue)" }}
                      />
                      {item}
                    </li>
                  ))}
                </ul>

                <div
                  className="mt-8 flex flex-col gap-2 border-t pt-6 text-[14.5px]"
                  style={{ borderColor: "var(--line)", color: "var(--muted)" }}
                >
                  <p data-testid="pricing-annual">
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>Annual billing:</span> 20% off.
                  </p>
                  <p data-testid="pricing-multi">
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>Multi-entity / group structures:</span>{" "}
                    <a href="mailto:vishwa363@gmail.com" className="link-u">contact us</a>.
                  </p>
                  <p data-testid="pricing-free-audit">
                    <span className="font-semibold" style={{ color: "var(--ink)" }}>Free 5-minute audit</span> before you decide.
                  </p>
                </div>

                <div className="mt-9 flex flex-col items-start gap-3 sm:flex-row sm:items-center">
                  <PrimaryButton testId="pricing-cta" label="Start with the free audit" />
                  <SecondaryButton
                    testId="pricing-secondary-cta"
                    label="Or book a 20-min call"
                    className="sm:ml-1"
                  />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const Founder = () => (
  <section
    id="founder"
    data-testid="section-founder"
    className="border-t"
    style={{ borderColor: "var(--line)", background: "var(--tint)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-28">
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-4">
          <Reveal>
            <div className="sec-num" data-testid="founder-eyebrow">
              <span>04 — Why now / why us</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <h2
              data-testid="founder-headline"
              className="display-h2 balance mt-5 text-3xl sm:text-4xl md:text-[44px]"
              style={{ color: "var(--ink)" }}
            >
              Built solo. By an engineer. For founders.
            </h2>
          </Reveal>
        </div>

        <div className="md:col-span-8">
          <div className="space-y-6 text-[17px] leading-relaxed md:text-lg" style={{ color: "var(--muted)" }}>
            <Reveal>
              <p data-testid="founder-p1">
                I&apos;m{" "}
                <span className="font-semibold" style={{ color: "var(--ink)" }}>
                  Vishwanath Sukumar
                </span>
                , building Adheran solo. Two years as Senior Engineer at Delivery Hero in Berlin watching enterprise compliance budgets in motion. Now at Wayfair. SMEs deserve a fraction of that capability without the price tag.
              </p>
            </Reveal>
            <Reveal delay={80}>
              <p data-testid="founder-p2">
                Pre-product, by design. I&apos;m running customer discovery with Austrian and German SMEs through May 2026. If you&apos;re wrestling with the AI Act, I want a 20-minute conversation before I write a line of production code.
              </p>
            </Reveal>
          </div>

          <Reveal delay={160}>
            <div
              data-testid="founder-contact"
              className="mt-10 flex flex-col gap-3 rounded-2xl border bg-white p-6 md:p-7"
              style={{ borderColor: "var(--line)" }}
            >
              <p className="eyebrow">Reachable</p>
              <div className="flex flex-col gap-3 text-[15.5px] sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-7 sm:gap-y-2">
                <a
                  href="mailto:vishwa363@gmail.com"
                  data-testid="founder-email"
                  className="link-u inline-flex items-center gap-2"
                >
                  <Mail className="h-4 w-4" aria-hidden="true" />
                  vishwa363@gmail.com
                </a>
                <a
                  href="tel:+4917684068330"
                  data-testid="founder-phone-de"
                  className="link-u inline-flex items-center gap-2"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  +49 176 8406 8330 <span className="text-neutral-500">(DE)</span>
                </a>
                <a
                  href="tel:+919035752486"
                  data-testid="founder-phone-in"
                  className="link-u inline-flex items-center gap-2"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" />
                  +91 90357 52486 <span className="text-neutral-500">(IN)</span>
                </a>
                <a
                  href="https://linkedin.com/in/vishwanath-sukumar"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="founder-linkedin"
                  className="link-u inline-flex items-center gap-2"
                  aria-label="Vishwanath Sukumar on LinkedIn"
                >
                  <Linkedin className="h-4 w-4" aria-hidden="true" />
                  LinkedIn
                </a>
              </div>
            </div>
          </Reveal>

          <Reveal delay={220}>
            <div className="mt-8">
              <PrimaryButton testId="founder-cta" label="Start your free AI Act audit" />
              <SecondaryButton testId="founder-secondary-cta" label="Or book a 20-min call instead" className="mt-3" />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const LawyerSection = () => (
  <section
    id="lawyer"
    data-testid="section-lawyer"
    className="border-t"
    style={{ borderColor: "var(--line)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-20 md:px-10 md:py-24">
      <div className="grid gap-12 md:grid-cols-12 md:gap-16">
        <div className="md:col-span-4">
          <Reveal>
            <div className="sec-num" data-testid="lawyer-eyebrow">
              <span>05 — Your lawyer + Adheran</span>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <span
              className="mt-6 inline-flex h-11 w-11 items-center justify-center rounded-xl"
              style={{ background: "var(--blue-50)", color: "var(--blue)" }}
              aria-hidden="true"
            >
              <Scale className="h-5 w-5" strokeWidth={2} />
            </span>
          </Reveal>
        </div>
        <div className="md:col-span-8">
          <Reveal>
            <h2
              data-testid="lawyer-headline"
              className="display-h2 balance text-3xl sm:text-4xl md:text-5xl"
              style={{ color: "var(--ink)" }}
            >
              We don&apos;t replace your lawyer.{" "}
              <span style={{ color: "var(--red)" }}>We cut their bill.</span>
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p
              data-testid="lawyer-body"
              className="pretty mt-7 max-w-2xl text-[17px] leading-relaxed md:text-lg"
              style={{ color: "var(--muted)" }}
            >
              Adheran generates the first-pass compliance documents, mapped to AI Act articles. Your lawyer reviews and signs off. Most SMEs save{" "}
              <span className="font-semibold" style={{ color: "var(--ink)" }}>
                80% of the billable hours
              </span>{" "}
              that would otherwise go into building these documents from scratch.
            </p>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const TrustBand = () => (
  <section
    data-testid="section-trust"
    className="border-t"
    style={{ borderColor: "var(--line)", background: "var(--ink)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10 md:py-12">
      <div className="grid gap-6 text-[14px] md:grid-cols-2 md:items-center md:text-[14.5px]">
        <p data-testid="trust-line-1" className="text-neutral-300 leading-relaxed">
          <span className="font-semibold text-white">EU-hosted (Frankfurt)</span>
          <span className="mx-2 text-neutral-500">·</span>
          GDPR-aligned
          <span className="mx-2 text-neutral-500">·</span>
          Built for the August 2, 2026 enforcement deadline.
        </p>
        <p data-testid="trust-line-2" className="text-neutral-400 md:text-right">
          Documents map to Annex III, Article 50, and Article 13 — designed to be reviewed by your lawyer or DPO.
        </p>
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer
    data-testid="site-footer"
    className="border-t"
    style={{ borderColor: "var(--line)", background: "var(--bg)" }}
  >
    <div className="mx-auto max-w-6xl px-6 py-10 md:px-10 md:py-12">
      <div className="flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
        <Wordmark />
        <div className="flex flex-col gap-2 text-[14px] md:flex-row md:items-center md:gap-7" style={{ color: "var(--muted)" }}>
          <span data-testid="footer-copy">© 2026 Adheran</span>
          <a
            href="mailto:vishwa363@gmail.com"
            data-testid="footer-email"
            className="link-u inline-flex items-center gap-2"
          >
            <Mail className="h-3.5 w-3.5" aria-hidden="true" />
            vishwa363@gmail.com
          </a>
          <a
            href="https://linkedin.com/in/vishwanath-sukumar"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-linkedin"
            className="link-u inline-flex items-center gap-2"
          >
            <Linkedin className="h-3.5 w-3.5" aria-hidden="true" />
            LinkedIn
          </a>
        </div>
      </div>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App" data-testid="app-root">
      <Nav />
      <main>
        <Hero />
        <Timeline />
        <Problem />
        <HowItWorks />
        <Pricing />
        <Founder />
        <LawyerSection />
        <TrustBand />
      </main>
      <Footer />
    </div>
  );
}

export default App;
